/* eslint-disable camelcase */
import React, {
  useState,
  useMemo,
  useEffect,
} from 'react';
import {
  Elements,
  StripeProvider,
} from 'react-stripe-elements';
import {
  Link,
} from 'react-router-dom';
import {
  getStatus,
} from 'redux-resource';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreUtils,
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import config from 'web-config';
import {
  getUiState,
} from 'web-selectors';
import {
  uiActions,
} from 'web-actions';

import {
  Modal,
  Spinner,
} from 'web-components';

import {
  Flex,
  Text,
  H200,
  TextHeavy,
} from 'web-styled';

import Buy from './Buy';
import Radio from './Radio';
import * as S from './styled';

const isFloat = (n: number) => Number(n) === n && n % 1 !== 0;
const getAmount = (amount: number) => (isFloat(amount)
  ? amount.toFixed(2)
  : amount);

const resourceTypes = [
  'workoutExercises',
  'personalWarmups',
  'personalWorkouts',
  'personalCooldowns',
  'personalPrograms',
];

const mapState = (state) => {
  let items = coreSelectors.getIntents(state);
  const checkoutModal = getUiState('checkoutModal')(state);

  if (checkoutModal.planId) {
    items = coreSelectors.getResourceMappedList(
      'productPlans',
    )(state);
  }

  return {
    items,
    loadStatus: getStatus(
      state,
      resourceTypes.map(
        r => `${r}.requests.loadResources.status`,
      ),
      false,
    ),
    email: coreSelectors.getProfile('email')(state),
    checkoutModal,
    subscription: coreSelectors.getSubscription(state),
  };
};

type Props = {
  isUpgrade?: boolean
  fetchAfterPay?: boolean,
  closeAfterFinish?: boolean,
  onFinish?: { (): void }
};

const Checkout = ({
  isUpgrade,
  fetchAfterPay,
  closeAfterFinish,
  onFinish,
}: Props) => {
  const {
    email,
    items,
    loadStatus,
    subscription,
    checkoutModal,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const [isFinish, setIsFinish] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(
    items.find(plan => plan.id === checkoutModal.planId),
  );

  useEffect(() => {
    if (checkoutModal.planId) {
      setSelectedPlan(
        items.find(plan => plan.id === checkoutModal.planId),
      );
    }
  }, [checkoutModal.planId]);

  const total = useMemo(() => {
    if (selectedPlan) {
      return selectedPlan.amount;
    }

    return items.reduce((a, b) => (a + b.amount), 0);
  }, [selectedPlan]);

  const onRemove = (id: number) => {
    if ((selectedPlan)) {
      dispatch(
        uiActions.setUiState({
          checkoutModal: {
            ...checkoutModal,
            planId: null,
          },
        }),
      );
    } else {
      dispatch(
        coreActions.deleteResources({
          resourceType: 'paymentIntents',
          resources: [{
            id,
          }],
        }),
      );
    }
  };

  const onRequestClose = () => {
    setIsFinish(false);
    if (!checkoutModal.planId) {
      dispatch(
        coreActions.deleteResources({
          resourceType: 'paymentIntents',
          resources: items.map(
            i => ({
              id: i.id,
            }),
          ),
        }),
      );
    }
    dispatch(
      uiActions.setUiState({
        checkoutModal: {
          isOpen: false,
        },
      }),
    );
  };

  const loadResources = () => {
    dispatch(
      coreActions.requestFetchResources({
        requestKey: 'loadResources',
        callbacks: {
          onSuccess: () => {
            setIsFinish(true);
          },
        },
      }),
    );
  };

  const handleAfterPay = (intent) => {
    if (isUpgrade) onFinish?.();
    else if (checkoutModal.planId) loadResources();
    else {
      dispatch(
        coreActions.requestFinishBuy({
          paymentIntentId: intent?.id,
          callbacks: {
            onSuccess: () => {
              if (onFinish) onFinish();
              if (fetchAfterPay) loadResources();
              if (closeAfterFinish) onRequestClose();
            },
          },
        }),
      );
    }
  };

  return (
    <Modal
      title="Checkout"
      isOpen={checkoutModal?.isOpen}
      onRequestClose={onRequestClose}
      contentStyle={{
        width: '580px',
      }}
    >
      {loadStatus.pending && (
        <Spinner
          over
          message="Loading..."
        />
      )}
      <S.Checkout>
        <Flex
          column
          spacing={5}
          width="100%"
        >
          <Flex
            width="100%"
            justify="space-between"
          >
            <Text color="#111">
              Product
            </Text>
            <Text color="#111">
              Price
            </Text>
          </Flex>
          <Flex
            column
            width="100%"
          >
            {items.map(item => (
              <S.Item key={item.id}>
                <Flex
                  width="100%"
                  align="center"
                  justify="space-between"
                >
                  {item.interval ? (
                    <Radio
                      id={item.id}
                      label={item.name || item.product_data?.name || item.invoice_data?.description}
                      value={item.id}
                      checked={item.id === selectedPlan?.id}
                      disabled={subscription.plan.id === item.stripe_id}
                      onChange={(ev) => {
                        setSelectedPlan(
                          items.find(plan => plan.id === Number(ev.target.value)),
                        );
                      }}
                    />
                  ) : (
                    <Text
                      color="#797979"
                    >
                      {item.name || item.product_data?.name || item.invoice_data?.description}
                    </Text>
                  )}

                  <Text
                    size="13px"
                  >
                    {coreUtils.getCurrencySymbol(item.currency) || '$'}
                    {getAmount(checkoutModal.planId ? item.amount : (item.amount || 0) / 100)}
                    {checkoutModal.planId && (
                      <small>
                        /
                        {coreUtils.getIntervalABBR(item.interval)}
                      </small>
                    )}
                  </Text>
                </Flex>
                {(items.length > 1 && !item.interval && !onFinish) && (
                  <S.RemoveItem onClick={() => onRemove(item.id)}>
                    Remove
                  </S.RemoveItem>
                )}
              </S.Item>
            ))}
          </Flex>
          <Flex
            width="100%"
            justify="space-between"
          >
            <TextHeavy color="#111">
              Total
            </TextHeavy>
            <TextHeavy color="#111">
              $
              {getAmount(checkoutModal.planId ? total : total / 100)}
            </TextHeavy>
          </Flex>
        </Flex>
        {isFinish ? (
          <Flex
            column
            spacing={15}
            margin="15px 0"
          >
            <Flex
              column
              spacing={10}
            >
              <H200 color="primary">
                Thank You!
              </H200>
              <Text>
                Order details will be sent to
                {' '}
                <b>
                  {email}
                </b>
                . Your content should be loading now and may take a few minutes
                to become accessible.
              </Text>
            </Flex>
            <Flex
              width="100%"
              justify="space-between"
            >
              <Link to="/personal_workouts">
                <Text
                  size="15px"
                  color="primary"
                >
                  My Workouts
                </Text>
              </Link>
              <Link to="/personal_programs">
                <Text
                  size="15px"
                  color="primary"
                >
                  My Programs
                </Text>
              </Link>
              <Link to="/account/transactions">
                <Text
                  size="15px"
                  color="primary"
                >
                  Transaction History
                </Text>
              </Link>
            </Flex>
          </Flex>
        ) : (
          <StripeProvider
            apiKey={config.stripe.key}
          >
            <Elements>
              <Buy
                planId={selectedPlan?.id}
                disabled={subscription.plan?.id === selectedPlan?.stripe_id}
                isUpgrade={isUpgrade}
                clientSecret={items[0]?.client_secret}
                subscriptionId={subscription.id}
                onSuccess={handleAfterPay}
              />
            </Elements>
          </StripeProvider>
        )}
      </S.Checkout>
    </Modal>
  );
};

Checkout.defaultProps = {
  isUpgrade: false,
  fetchAfterPay: false,
  closeAfterFinish: false,
  onFinish: null,
};

export default Checkout;
